import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import SEO from '../components/SEO';

const MedicalContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
`;

const PageHeader = styled.div`
  text-align: center;
  margin-bottom: 3rem;
  background: linear-gradient(135deg, ${props => props.theme.colors.primary}, ${props => props.theme.colors.secondary});
  color: white;
  padding: 3rem 2rem;
  border-radius: 16px;
  margin: 0 0 3rem 0;
  
  @media (max-width: 767px) {
    margin: 0 -2rem 3rem -2rem;
    border-radius: 0;
  }
`;

const PageTitle = styled.h1`
  font-size: 3rem;
  margin-bottom: 1rem;
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    font-size: 2rem;
  }
`;

const PageSubtitle = styled.p`
  font-size: 1.2rem;
  opacity: 0.9;
  max-width: 600px;
  margin: 0 auto;
`;

const MedicalSection = styled(motion.section)`
  background: white;
  padding: 2rem;
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0,0,0,0.1);
  margin-bottom: 2rem;
`;

const SectionTitle = styled.h2`
  color: ${props => props.theme.colors.primary};
  margin-bottom: 1.5rem;
  font-size: 1.8rem;
`;

const MedicalText = styled.p`
  line-height: 1.6;
  margin-bottom: 1rem;
  color: ${props => props.theme.colors.dark};
`;

const StatGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1.5rem;
  margin: 2rem 0;
`;

const StatCard = styled.div`
  text-align: center;
  padding: 1.5rem;
  background: ${props => props.theme.colors.light};
  border-radius: 8px;
  border-left: 4px solid ${props => props.theme.colors.accent};
`;

const StatNumber = styled.div`
  font-size: 2.5rem;
  font-weight: bold;
  color: ${props => props.theme.colors.accent};
  margin-bottom: 0.5rem;
`;

const StatLabel = styled.div`
  color: ${props => props.theme.colors.gray};
  font-weight: bold;
`;

const TimelineList = styled.ul`
  list-style: none;
  padding: 0;
`;

const TimelineItem = styled.li`
  margin-bottom: 1rem;
  padding: 1rem;
  background: ${props => props.theme.colors.light};
  border-radius: 8px;
  border-left: 4px solid ${props => props.theme.colors.secondary};
`;

const ItemTime = styled.strong`
  color: ${props => props.theme.colors.primary};
  display: block;
  margin-bottom: 0.5rem;
`;

const WarningBox = styled.div`
  background: #fff5f5;
  border: 1px solid #fed7d7;
  border-radius: 8px;
  padding: 1.5rem;
  margin: 2rem 0;
  
  h3 {
    color: #c53030;
    margin-bottom: 1rem;
  }
  
  p {
    color: #744210;
    margin-bottom: 0.5rem;
  }
`;

const NextSectionContainer = styled.div`
  text-align: center;
  margin-top: 4rem;
  padding: 2rem;
`;

const NextSectionLink = styled(Link)`
  display: inline-block;
  background: linear-gradient(135deg, ${props => props.theme.colors.primary}, ${props => props.theme.colors.secondary});
  color: white;
  padding: 1rem 2rem;
  border-radius: 8px;
  text-decoration: none;
  font-weight: bold;
  transition: transform 0.2s ease;
  
  &:hover {
    transform: translateY(-2px);
  }
`;

const NextSectionText = styled.p`
  margin-bottom: 1rem;
  color: ${props => props.theme.colors.gray};
`;

const Medical = () => {
  return (
    <MedicalContainer>
      <SEO 
        title="Medical Details & Emergency Response"
        description="Detailed medical account of Maggie's sudden cardiac arrest, 26-minute CPR response, and miraculous recovery. Learn about the medical interventions, emergency response coordination, and why doctors called it a 5% survival case."
        url="/medical"
        image="/uploads/EMTS_AT_WORK.jpg"
        type="article"
      />
      <PageHeader>
        <PageTitle>Medical Information</PageTitle>
        <PageSubtitle>
          Understanding the medical emergency, treatment, and recovery process 
          that made Maggie's survival truly miraculous
        </PageSubtitle>
      </PageHeader>

      <MedicalSection
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        viewport={{ once: true }}
      >
        <SectionTitle>The Medical Emergency</SectionTitle>
        <MedicalText>
          On November 28, 2024, at approximately 12:20 PM, Maggie Soward experienced 
          sudden ventricular tachycardia that rapidly progressed to cardiac arrest. 
          Ventricular tachycardia (V-tach) is a dangerous heart rhythm that originates 
          in the ventricles and can quickly become life-threatening.
        </MedicalText>
        <MedicalText>
          The progression from V-tach to cardiac arrest happened within seconds. 
          Maggie collapsed immediately, showing signs of seizure-like activity, 
          graying of the skin, and complete loss of consciousness - all classic 
          signs of sudden cardiac arrest.
        </MedicalText>

        <StatGrid>
          <StatCard>
            <StatNumber>6%</StatNumber>
            <StatLabel>Survival Rate for Out-of-Hospital Cardiac Arrest</StatLabel>
          </StatCard>
          <StatCard>
            <StatNumber>26</StatNumber>
            <StatLabel>Minutes Without Pulse</StatLabel>
          </StatCard>
          <StatCard>
            <StatNumber>3</StatNumber>
            <StatLabel>Defibrillation Shocks</StatLabel>
          </StatCard>
          <StatCard>
            <StatNumber>5</StatNumber>
            <StatLabel>Minutes of Family CPR</StatLabel>
          </StatCard>
        </StatGrid>
      </MedicalSection>

      <MedicalSection
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.2 }}
        viewport={{ once: true }}
      >
        <SectionTitle>Life-Saving Interventions</SectionTitle>
        
        <TimelineList>
          <TimelineItem>
            <ItemTime>12:21 PM - Immediate CPR</ItemTime>
            Scott attempted initial CPR while Isaac called 911 and summoned Eliza, 
            who had recently recertified in CPR.
          </TimelineItem>
          
          <TimelineItem>
            <ItemTime>12:22 PM - Continuous Chest Compressions</ItemTime>
            Eliza began professional-quality chest compressions, maintaining them 
            for nearly 5 minutes until first responders arrived.
          </TimelineItem>
          
          <TimelineItem>
            <ItemTime>12:25 PM - Professional Response</ItemTime>
            Nine first responders arrived and immediately took over resuscitation efforts, 
            including advanced airway management and medication administration.
          </TimelineItem>
          
          <TimelineItem>
            <ItemTime>12:26-12:50 PM - Advanced Life Support</ItemTime>
            Multiple rounds of chest compressions, three defibrillation attempts, 
            and advanced cardiac life support protocols were employed.
          </TimelineItem>
          
          <TimelineItem>
            <ItemTime>12:50 PM - Return of Pulse</ItemTime>
            After 26 minutes of continuous resuscitation efforts, a weak but 
            detectable pulse was restored.
          </TimelineItem>
        </TimelineList>
      </MedicalSection>

      <MedicalSection
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.4 }}
        viewport={{ once: true }}
      >
        <SectionTitle>Hospital Care and Recovery</SectionTitle>
        <MedicalText>
          Maggie was transported to Holy Cross Hospital - Mountain Point where she received intensive 
          care treatment. The medical team, including Dr. Ghali, Dr. Al Jandali, and 
          Nurse Andrew, provided round-the-clock monitoring and treatment.
        </MedicalText>
        <MedicalText>
          Key aspects of her hospital care included:
        </MedicalText>
        <ul style={{ marginLeft: '2rem', marginBottom: '1rem' }}>
          <li>Mechanical ventilation to support breathing</li>
          <li>Continuous cardiac monitoring</li>
          <li>Neurological assessments to check for brain damage</li>
          <li>Intravenous medications to support heart function</li>
          <li>Temperature management and other supportive care</li>
        </ul>
        
        <MedicalText>
          The most remarkable aspect of Maggie's recovery was the absence of significant 
          neurological damage despite the extended period without circulation. This is 
          extremely rare and is largely attributed to the immediate and continuous CPR 
          provided by her family.
        </MedicalText>
      </MedicalSection>

      <WarningBox>
        <h3>Important Medical Information</h3>
        <p>
          <strong>Learn CPR:</strong> Maggie's survival was directly due to immediate CPR. 
          Brain damage can occur within 4-6 minutes without oxygen.
        </p>
        <p>
          <strong>Call 911 Immediately:</strong> Never hesitate to call emergency services 
          for someone who is unresponsive.
        </p>
        <p>
          <strong>Know the Signs:</strong> Sudden collapse, no pulse, no breathing, 
          and unresponsiveness are signs of cardiac arrest.
        </p>
        <p>
          <strong>AED Training:</strong> Automated External Defibrillators can restore 
          normal heart rhythm and are available in many public places.
        </p>
      </WarningBox>

      <MedicalSection
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.6 }}
        viewport={{ once: true }}
      >
        <SectionTitle>Why This Was Miraculous</SectionTitle>
        <MedicalText>
          Several factors make Maggie's survival truly extraordinary:
        </MedicalText>
        <ul style={{ marginLeft: '2rem', marginBottom: '1rem' }}>
          <li>
            <strong>Survival Rate:</strong> Only 6% of out-of-hospital cardiac arrest 
            victims survive to discharge without neurological impairment.
          </li>
          <li>
            <strong>Duration:</strong> 26 minutes without a detectable pulse is far 
            beyond typical survival times.
          </li>
          <li>
            <strong>Neurological Outcome:</strong> Maggie recovered with no apparent 
            brain damage, which is extremely rare.
          </li>
          <li>
            <strong>Age and Health:</strong> While Maggie was relatively young and healthy, 
            sudden cardiac arrest can happen to anyone.
          </li>
          <li>
            <strong>Chain of Survival:</strong> Every link in the chain worked perfectly - 
            immediate recognition, CPR, emergency response, and advanced care.
          </li>
        </ul>
        
        <MedicalText>
          Medical professionals have called Maggie's case a "textbook example" of how 
          proper CPR and emergency response can save a life, even in the most dire circumstances.
        </MedicalText>
      </MedicalSection>

      <NextSectionContainer>
        <NextSectionText>Continue exploring Maggie's story</NextSectionText>
        <NextSectionLink to="/timeline">View Complete Timeline</NextSectionLink>
      </NextSectionContainer>
    </MedicalContainer>
  );
};

export default Medical;
